import { useMutation, useQuery } from "@tanstack/react-query";
import {
  deleteCoachingClip,
  getCoachingClips,
  updateCoachingClip,
  type ApiError,
} from "@/lib/api";
import type { CoachingVisibility } from "@/lib/types";
import { invalidateScope, useScopedKey } from "@/lib/queryClient";
import { useScopeStore } from "@/stores/scope";
import { settleBulk, type BulkResult } from "./bulkOps";

/**
 * Coach > Clips list-page data hooks (M6a).
 *
 * `useCoachClipsList` is the team's coaching clips for the active
 * scope. `useCoachClipMutations` covers the one list-page write
 * path — delete. (Create + edit go through Coach Review's
 * `useSaveCoachingClip` — the Clips list routes INTO Coach Review
 * for those, same pattern as the Notes list.)
 *
 * Scope-keyed + gated on `teamId`, like the other M6a list
 * queries; the delete `invalidateScope`s so the list refetches.
 */

export function useCoachClipsList(options?: { enabled?: boolean }) {
  const teamId = useScopeStore((s) => s.teamId);
  return useQuery({
    queryKey: useScopedKey("coaching-clips"),
    // Wrap so react-query's QueryFunctionContext isn't forwarded as
    // the optional `matchId` arg — the list page wants ALL clips.
    queryFn: () => getCoachingClips(),
    enabled: !!teamId && (options?.enabled ?? true),
  });
}

export type CoachClipMutations = {
  deleteClip: (id: number) => Promise<{ ok: true }>;
  /** A6: bulk visibility change — PATCHes each id with only
   * `{ visibility }`, then invalidates ONCE. */
  bulkUpdateVisibility: (
    ids: number[],
    visibility: CoachingVisibility,
  ) => Promise<BulkResult>;
  /** A6: bulk delete — DELETEs each id, then invalidates ONCE. */
  bulkDelete: (ids: number[]) => Promise<BulkResult>;
  isPending: boolean;
  error: ApiError | null;
};

export function useCoachClipMutations(): CoachClipMutations {
  const remove = useMutation({
    mutationFn: deleteCoachingClip,
    onSuccess: () => invalidateScope(),
  });
  const bulkVisibility = useMutation({
    mutationFn: ({
      ids,
      visibility,
    }: {
      ids: number[];
      visibility: CoachingVisibility;
    }) =>
      settleBulk(ids, (id) => updateCoachingClip(id, { visibility })),
    onSuccess: () => invalidateScope(),
  });
  const bulkRemove = useMutation({
    mutationFn: (ids: number[]) =>
      settleBulk(ids, (id) => deleteCoachingClip(id)),
    onSuccess: () => invalidateScope(),
  });

  const all = [remove, bulkVisibility, bulkRemove];
  return {
    deleteClip: (id) => remove.mutateAsync(id),
    bulkUpdateVisibility: (ids, visibility) =>
      bulkVisibility.mutateAsync({ ids, visibility }),
    bulkDelete: (ids) => bulkRemove.mutateAsync(ids),
    isPending: all.some((m) => m.isPending),
    error: (all.find((m) => m.error)?.error as ApiError | undefined) ?? null,
  };
}
